import { gql } from '@apollo/client';
import Dropdown from '@orn/shared-web/components/Dropdown';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { CalendarIcon, EditIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import AccountMetadata from '../../AccountMetadata';
import { isUnhealthyBankConnectionMetadata } from '../../CalloutCard/cards/UnhealthyBankConnectionCalloutCard';
import { DateRangePicker, DateRangePickerModal, type CalendarDateRange } from '../../DateRangePicker';
import Entity from '../../entity';
import PlaidFixConnectionButton from '../../plaid/PlaidFixConnectionButton';
import UpdateAccountModal from '../../UpdateAccountModal';

import type { AccountPaneHeaderFragment } from './__generated__/AccountPaneHeader.gql';

const AccountPaneHeaderFragment = gql`
  fragment AccountPaneHeader on FinancialAccount {
    id
    name
    subtype
    mask
    ...EntityAvatar
    bankConnection {
      id
      lastSyncedAt
      unhealthyConnectionCallout {
        id
        metadata
      }
    }
    bank {
      id
      name
    }
  }
  ${Entity.Avatar.Fragment}
`;

interface IAccountPaneHeaderProps {
  financialAccount: AccountPaneHeaderFragment;
  dateRange?: CalendarDateRange;
  onDateRangeChange?: (dateRange: CalendarDateRange) => void;
}

/**
 * Pane header for a single financial account. Renders the account avatar, name,
 * inline metadata (subtype, bank, mask, sync status) and trailing actions
 * (date range, reconnect prompt, edit menu). At `<md` the avatar shrinks to
 * `lg` (48px) and the sync status drops to its own line below the meta row to
 * match the Figma Account "Section Header" layout. The inline date range
 * picker is hidden at `<md` and remains accessible via the dots-menu.
 */
const AccountPaneHeader = ({ financialAccount, dateRange, onDateRangeChange }: IAccountPaneHeaderProps) => {
  const isMdOrUp = useBreakpoint('md');
  const [isUpdateModalOpen, setIsUpdateModalOpen] = React.useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = React.useState(false);

  // Check if bank connection has an unhealthy callout requiring login
  const unhealthyCallout = financialAccount.bankConnection?.unhealthyConnectionCallout;
  const needsReconnection =
    unhealthyCallout &&
    isUnhealthyBankConnectionMetadata(unhealthyCallout.metadata) &&
    unhealthyCallout.metadata.subtype === 'login_required';

  return (
    <>
      <Entity.Header
        title={financialAccount.name}
        subtitle={
          <AccountMetadata
            subtype={financialAccount.subtype}
            bankName={financialAccount.bank.name}
            mask={financialAccount.mask}
            lastSyncedAt={financialAccount.bankConnection?.lastSyncedAt ?? undefined}
            syncStatusPlacement="stacked-below"
          />
        }
        avatar={<Entity.Avatar entity={financialAccount} size={isMdOrUp ? 'xl' : 'lg'} />}
        trailingContent={
          <div className="flex items-center gap-3">
            {needsReconnection && (
              <PlaidFixConnectionButton bankConnectionId={financialAccount.bankConnection.id} size="sm" />
            )}
            {isMdOrUp && <DateRangePicker value={dateRange} onChange={onDateRangeChange} defaultValue="past6Months" />}
            <Dropdown.Root>
              <Dropdown.DotsButton className="p-2" />
              <Dropdown.Popover className="w-min">
                <Dropdown.Menu>
                  {!isMdOrUp && (
                    <Dropdown.Item icon={CalendarIcon} onAction={() => setIsDateRangeModalOpen(true)}>
                      Time range&hellip;
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item icon={EditIcon} onAction={() => setIsUpdateModalOpen(true)}>
                    Edit account&hellip;
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          </div>
        }
      />
      <UpdateAccountModal account={financialAccount} isOpen={isUpdateModalOpen} onOpenChange={setIsUpdateModalOpen} />
      {!isMdOrUp && (
        <DateRangePickerModal
          isOpen={isDateRangeModalOpen}
          onOpenChange={setIsDateRangeModalOpen}
          value={dateRange}
          onChange={onDateRangeChange}
          defaultValue="past6Months"
        />
      )}
    </>
  );
};

AccountPaneHeader.Fragment = AccountPaneHeaderFragment;

export default AccountPaneHeader;
